import React, { useCallback, useEffect, useRef, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * BeforeAfterSlider
 * Drag the handle (or click anywhere) to reveal more or less of the "before" image.
 * - beforeSrc, afterSrc: required
 * - aspect: tailwind aspect class (default 'aspect-[4/3]')
 */
export default function BeforeAfterSlider({ beforeSrc, afterSrc, aspect = "aspect-[4/3]", className = "" }) {
  const wrapRef = useRef(null);
  const [pct, setPct] = useState(50);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const next = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPct(next);
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    dragging.current = false;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (err) {
      // releasePointerCapture throws if the pointer was never captured
      // (e.g. user lifted finger off the element). Non-fatal — log for debug.
      if (typeof console !== "undefined") console.debug("[slider] releasePointerCapture:", err?.name || err);
    }
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPct((p) => Math.max(0, p - 5));
    if (e.key === "ArrowRight") setPct((p) => Math.min(100, p + 5));
  };

  // Lazy: reset slider position when sources change
  useEffect(() => { setPct(50); }, [beforeSrc, afterSrc]);

  return (
    <div
      ref={wrapRef}
      data-testid="before-after-slider"
      className={`relative ${aspect} w-full select-none overflow-hidden bg-slate-100 rounded-sm cursor-ew-resize ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      role="slider"
      aria-label="Before / after image comparison"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      {/* AFTER (base) */}
      <img
        src={afterSrc}
        alt="After"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      {/* BEFORE (clipped to left of handle) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pct}%` }}
      >
        <img
          src={beforeSrc}
          alt="Before"
          draggable={false}
          className="absolute inset-0 h-full w-auto max-w-none object-cover pointer-events-none"
          style={{ width: wrapRef.current ? `${wrapRef.current.getBoundingClientRect().width}px` : "100%" }}
        />
      </div>

      {/* Corner labels */}
      <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.24em] font-semibold bg-navy/85 text-white px-2 py-1 rounded-sm pointer-events-none">Before</span>
      <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.24em] font-semibold bg-white/90 text-navy px-2 py-1 rounded-sm pointer-events-none">After</span>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white pointer-events-none"
        style={{ left: `${pct}%`, transform: "translateX(-0.5px)" }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg border border-navy/20 flex items-center justify-center pointer-events-none"
        style={{ left: `${pct}%`, transform: "translate(-50%, -50%)" }}
      >
        <CaretLeft size={14} weight="bold" className="text-navy" />
        <CaretRight size={14} weight="bold" className="text-navy -ml-1" />
      </div>
    </div>
  );
}
